import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import simpleCacheStorage from '@services/app/simple-cache-storage';
import { getRawMaterials } from '@services/raw-materials/raw-materials-api';
import { RawMaterial } from '@interfaces/raw-material';

const KEY = 'NC_RAW_MATERIALS';

interface Props {
  children: ReactNode;
}

interface IContextProps {
  rawMaterials: RawMaterial[];
  setRawMaterials: Dispatch<SetStateAction<RawMaterial[]>>;
  getRawMaterials: () => Promise<boolean>;
}

// Context
const RawMaterialsContext = createContext({} as IContextProps);

// Provider
export const RawMaterialsProvider = ({ children }: Props): JSX.Element => {
  const [
    rawMaterials,
    setRawMaterials
  ] = useState(simpleCacheStorage.getItem(KEY) || []);

  const onUpdate = (items) => {
    // simpleCacheStorage.setItem(KEY, items, 60);
    setRawMaterials(items);
  };

  // dont return the values and instead use state
  const onGet = () => {
    if (rawMaterials.length >= 1) {
      return Promise.resolve(true);
    }

    return getRawMaterials()
      .then((result) => {
        onUpdate(result);
        return true;
      })
      .catch((e) => {
        console.log(e);
        return false;
      });
  }

  // context value object
  const value: IContextProps = useMemo(() => {
    return {
      rawMaterials,
      setRawMaterials: onUpdate,
      getRawMaterials: onGet,
    };
  }, [rawMaterials, setRawMaterials, onUpdate, simpleCacheStorage, KEY]);

  return (
    <RawMaterialsContext.Provider value={value}>{children}</RawMaterialsContext.Provider>
  );
};

// useRawMaterials hook
export const useRawMaterials = (): IContextProps => {
  const context: IContextProps = useContext(RawMaterialsContext);
  if (context === null || context === undefined) {
    throw new Error(
      'You probably forgot the <RawMaterialsProvider> context provider'
    );
  }
  return context;
};